// Friends Management for Firestore
// Handles adding, removing, and retrieving friends

import { getFirestore, doc, setDoc, getDoc, deleteDoc, collection, query, where, getDocs, arrayUnion, arrayRemove, updateDoc } from "firebase/firestore";

export const FriendsManager = {
  // Add a friend to the current user's friends list
  addFriend: async (currentUserId, friendData, db) => {
    if (!currentUserId || !friendData || !db) {
      throw new Error('Missing required parameters');
    }

    try {
      const userFriendsRef = doc(db, 'users', currentUserId, 'friends', friendData.userId || friendData.username);
      
      const friendRecord = {
        userId: friendData.userId,
        username: friendData.username,
        displayName: friendData.displayName || friendData.username,
        phoneNumber: friendData.phoneNumber || null,
        profilePicture: friendData.profilePicture || null,
        addedAt: new Date().toISOString(),
        // Store how we found them for context
        addedBy: friendData.phoneNumber ? 'phone' : 'username'
      };

      await setDoc(userFriendsRef, friendRecord);

      // Also add to a quick lookup array in the user's main document
      const userRef = doc(db, 'userProfiles', currentUserId);
      try {
        await updateDoc(userRef, {
          friends: arrayUnion(friendData.userId || friendData.username),
          updatedAt: new Date().toISOString()
        });
      } catch (error) {
        // If user profile doesn't exist, create it
        if (error.code === 'not-found') {
          await setDoc(userRef, {
            userId: currentUserId,
            friends: [friendData.userId || friendData.username],
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString()
          });
        } else {
          throw error;
        }
      }

      // Add reverse friendship so both users see each other
      const friendUserId = friendData.userId;
      if (friendUserId) {
        try {
          // Get current user's profile to build the reverse record
          const currentUserProfile = await getDoc(doc(db, 'userProfiles', currentUserId));
          const currentUserData = currentUserProfile.exists() ? currentUserProfile.data() : {};

          const reverseRecord = {
            userId: currentUserId,
            username: currentUserData.username || '',
            displayName: currentUserData.displayName || currentUserData.username || '',
            phoneNumber: currentUserData.phoneNumber || null,
            profilePicture: currentUserData.profilePicture || null,
            addedAt: new Date().toISOString(),
            addedBy: 'mutual'
          };

          const reverseFriendsRef = doc(db, 'users', friendUserId, 'friends', currentUserId);
          await setDoc(reverseFriendsRef, reverseRecord, { merge: true });

          // Update friend's lookup array too
          const friendProfileRef = doc(db, 'userProfiles', friendUserId);
          try {
            await updateDoc(friendProfileRef, {
              friends: arrayUnion(currentUserId),
              updatedAt: new Date().toISOString()
            });
          } catch (e) {
            // Friend profile might not exist yet, that's ok
            console.log('Could not update reverse friend lookup:', e.message);
          }
        } catch (reverseError) {
          // Don't fail the whole operation if reverse add fails
          console.warn('Could not add reverse friendship:', reverseError.message);
        }
      }

      return friendRecord;
    } catch (error) {
      console.error('Error adding friend:', error);
      throw error;
    }
  },

  // Get all friends for the current user
  getFriends: async (currentUserId, db) => {
    if (!currentUserId || !db) return [];

    try {
      const friendsRef = collection(db, 'users', currentUserId, 'friends');
      const querySnapshot = await getDocs(friendsRef);
      
      const friends = [];
      querySnapshot.forEach((doc) => {
        friends.push({
          id: doc.id,
          ...doc.data()
        });
      });

      // Sort by display name
      return friends.sort((a, b) => 
        (a.displayName || a.username).localeCompare(b.displayName || b.username)
      );
    } catch (error) {
      console.error('Error getting friends:', error);
      return [];
    }
  },

  // Remove a friend
  removeFriend: async (currentUserId, friendId, db) => {
    if (!currentUserId || !friendId || !db) {
      throw new Error('Missing required parameters');
    }

    try {
      // Remove from friends subcollection
      const friendRef = doc(db, 'users', currentUserId, 'friends', friendId);
      await deleteDoc(friendRef);
      
      // Remove from quick lookup array
      const userRef = doc(db, 'userProfiles', currentUserId);
      try {
        await updateDoc(userRef, {
          friends: arrayRemove(friendId),
          updatedAt: new Date().toISOString()
        });
      } catch (error) {
        // If user profile doesn't exist, that's fine - friend is already removed
        if (error.code !== 'not-found') {
          throw error;
        }
      }

      return true;
    } catch (error) {
      console.error('Error removing friend:', error);
      throw error;
    }
  },

  // Check if someone is already a friend
  isFriend: async (currentUserId, friendUserId, db) => {
    if (!currentUserId || !friendUserId || !db) return false;

    try {
      const friendRef = doc(db, 'users', currentUserId, 'friends', friendUserId);
      const friendDoc = await getDoc(friendRef);
      return friendDoc.exists();
    } catch (error) {
      console.error('Error checking if user is friend:', error);
      return false;
    }
  },

  // Get friends for quick-add suggestions (returns simplified format)
  getFriendsForQuickAdd: async (currentUserId, db) => {
    try {
      const friends = await FriendsManager.getFriends(currentUserId, db);
      
      // Return in format suitable for bill creation
      return friends.map(friend => ({
        id: friend.userId || friend.id,
        name: friend.displayName || friend.username,
        username: friend.username,
        phoneNumber: friend.phoneNumber,
        isFriend: true
      }));
    } catch (error) {
      console.error('Error getting friends for quick-add:', error);
      return [];
    }
  },

  // Migrate and refresh existing friends with latest profile data
  migrateFriendsWithProfilePictures: async (currentUserId, db) => {
    if (!currentUserId || !db) return false;

    try {
      console.log('🔄 Starting friend migration for user:', currentUserId);
      
      const friendsRef = collection(db, 'users', currentUserId, 'friends');
      const querySnapshot = await getDocs(friendsRef);
      
      if (querySnapshot.empty) {
        console.log('No friends to migrate');
        return true;
      }

      let migratedCount = 0;
      const migrationPromises = [];

      querySnapshot.forEach((friendDoc) => {
        const friendData = friendDoc.data();
        
        console.log('🔍 Checking friend for migration:', {
          friendId: friendDoc.id,
          friendName: friendData.displayName || friendData.username,
          hasProfilePicture: !!friendData.profilePicture,
          userId: friendData.userId
        });

        // Create migration promise for this friend - always check for updates
        const migrationPromise = (async () => {
          try {
            // Fetch latest profile data from the correct path
            const profileDocRef = doc(db, `users/${friendData.userId}/profile/data`);
            const profileDoc = await getDoc(profileDocRef);
            
            if (profileDoc.exists()) {
              const profileData = profileDoc.data();
              
              // Enhanced debugging - show ALL fields in profile data
              console.log('🔍 DETAILED PROFILE DATA for', friendData.displayName || friendData.username, ':', {
                rawProfileData: profileData,
                allKeys: Object.keys(profileData),
                profilePictureField: profileData.profilePicture,
                displayNameField: profileData.displayName,
                hasProfilePictureKey: 'profilePicture' in profileData,
                hasDisplayNameKey: 'displayName' in profileData,
                profileDataType: typeof profileData
              });
              
              console.log('📝 Found profile data for friend:', {
                friendName: friendData.displayName || friendData.username,
                hasProfilePicture: !!profileData.profilePicture,
                currentDisplayName: profileData.displayName,
                storedDisplayName: friendData.displayName
              });
              
              // Check if we need to update anything
              const needsUpdate = 
                (profileData.profilePicture && profileData.profilePicture !== friendData.profilePicture) ||
                (profileData.displayName && profileData.displayName !== friendData.displayName);
              
              if (needsUpdate) {
                // Update the friend record with latest profile data
                const friendDocRef = doc(db, 'users', currentUserId, 'friends', friendDoc.id);
                const updates = {
                  migratedAt: new Date().toISOString()
                };
                
                if (profileData.profilePicture) {
                  updates.profilePicture = profileData.profilePicture;
                }
                if (profileData.displayName) {
                  updates.displayName = profileData.displayName;
                }
                
                await updateDoc(friendDocRef, updates);
                
                console.log('✅ Updated friend data:', {
                  friendName: friendData.displayName || friendData.username,
                  updatedFields: Object.keys(updates).filter(key => key !== 'migratedAt')
                });
                return true;
              } else {
                console.log('⏭️ Friend data already up to date:', friendData.displayName || friendData.username);
                return false;
              }
            } else {
              console.log('❌ No profile found for friend in profile/data, trying username registry:', {
                friendName: friendData.displayName || friendData.username,
                userId: friendData.userId,
                username: friendData.username
              });
              
              // Fallback: Try to find profile data via username in users collection
              if (friendData.username) {
                try {
                  const userDocRef = doc(db, 'users', friendData.username);
                  const userDoc = await getDoc(userDocRef);
                  
                  if (userDoc.exists()) {
                    const userData = userDoc.data();
                    console.log('🔄 Found friend in username registry, checking for profile updates:', {
                      friendName: friendData.displayName || friendData.username,
                      registryDisplayName: userData.displayName,
                      storedDisplayName: friendData.displayName
                    });
                    
                    // Check if we can update displayName from registry
                    if (userData.displayName && userData.displayName !== friendData.displayName) {
                      const friendDocRef = doc(db, 'users', currentUserId, 'friends', friendDoc.id);
                      await updateDoc(friendDocRef, {
                        displayName: userData.displayName,
                        migratedAt: new Date().toISOString()
                      });
                      
                      console.log('✅ Updated friend displayName from username registry:', {
                        friendName: friendData.displayName || friendData.username,
                        newDisplayName: userData.displayName
                      });
                      return true;
                    }
                  }
                } catch (registryError) {
                  console.log('Could not check username registry:', registryError);
                }
              }
              
              return false;
            }
          } catch (error) {
            console.error('❌ Error migrating friend:', friendData.displayName || friendData.username, error);
            return false;
          }
        })();

        migrationPromises.push(migrationPromise);
      });

      // Execute all migrations
      const results = await Promise.all(migrationPromises);
      migratedCount = results.filter(Boolean).length;
      
      console.log(`✅ Migration complete: ${migratedCount}/${querySnapshot.size} friends updated with latest profile data`);
      return true;
    } catch (error) {
      console.error('❌ Error during friend migration:', error);
      return false;
    }
  }
};